import { useState, useEffect } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { XMarkIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { Badge } from 'components/badge'
import { useTranslation } from 'react-i18next'

/**
 * ProgressDialog - A reusable dialog component for showing bulk operations with progress tracking
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {Function} props.onClose - Callback when dialog should close
 * @param {string} props.title - Dialog title
 * @param {string} props.description - Dialog description text
 * @param {Array} props.items - Array of items to process
 * @param {Function} props.onConfirm - Callback when user confirms the action
 * @param {boolean} props.isProcessing - Whether processing is in progress
 * @param {string} props.confirmText - Text for confirm button
 * @param {string} props.cancelText - Text for cancel button
 * @param {string} props.closeText - Text for close button (after completion)
 * @param {Object} props.icon - Icon component to display in header
 * @param {string} props.iconBgColor - Background color for icon (e.g., 'bg-red-100')
 * @param {string} props.iconColor - Icon color (e.g., 'text-red-600')
 * @param {Function} props.formatItemTitle - Function to format item title
 * @param {Function} props.formatItemSubtitle - Function to format item subtitle
 */
export function ProgressDialog(props) {
    const { t } = useTranslation()
    const {
        open,
        onClose,
        title,
        description,
        items = [],
        onConfirm,
        isProcessing = false,
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        closeText = 'Close',
        icon: Icon = XCircleIcon,
        iconBgColor = 'bg-red',
        iconColor = 'text-red',
        formatItemTitle = (item) => item.title || '-',
        formatItemSubtitle = (item) => item.subtitle || '-',
    } = props

    const [showDialog, setShowDialog] = useState(open)

    useEffect(() => {
        setShowDialog(open)
    }, [open])

    const handleClose = () => {
        if (!isProcessing) {
            setShowDialog(false)
            onClose?.()
        }
    }

    const handleConfirm = () => {
        onConfirm?.()
    }

    // Calculate progress
    const completedItems = items.filter(item => item.status === 'success' || item.status === 'error').length
    const progressPercentage = items.length > 0 ? Math.round((completedItems / items.length) * 100) : 0

    // Check if all items are still pending
    const allPending = items.every(item => item.status === 'pending')

    // Get status badge configuration
    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return { color: 'zinc', label: t('Pending') }
            case 'processing':
                return { color: 'blue', label: t('Processing') }
            case 'success':
                return { color: 'emerald', label: t('Success') }
            case 'error':
                return { color: 'red', label: t('Failed') }
            default:
                return { color: 'zinc', label: status }
        }
    }

    return (
        <Dialog open={showDialog} onClose={handleClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-3xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        {/* Dialog content */}
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div className="sm:flex sm:items-start">
                             
                                <div className="mt-3 w-full text-center sm:ml-4 sm:mt-0 sm:text-left">
                                    <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                        {title}
                                    </DialogTitle>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            {description}
                                        </p>
                                    </div>

                                    {/* Items List with Progress */}
                                    <div className="mt-4 max-h-96 overflow-y-auto">
                                        <div className="space-y-2">
                                            {items.map((item, index) => {
                                                const statusConfig = getStatusBadge(item.status)
                                                
                                                return (
                                                    <div
                                                        key={item.id || index}
                                                        className={`rounded-lg border p-3 transition-all duration-300 ${
                                                            item.status === 'success'
                                                                ? 'bg-green-50 border-green-200'
                                                                : item.status === 'error'
                                                                ? 'bg-red-50 border-red-200'
                                                                : item.status === 'processing'
                                                                ? 'bg-blue-50 border-blue-200'
                                                                : 'bg-gray-50 border-gray-200'
                                                        }`}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-sm text-gray-900">
                                                                        {formatItemTitle(item)}
                                                                    </span>
                                                                    
                                                                    {/* Status Icon */}
                                                                    {item.status === 'processing' && (
                                                                        <svg 
                                                                            className="animate-spin h-4 w-4 text-blue-600" 
                                                                            xmlns="http://www.w3.org/2000/svg" 
                                                                            fill="none" 
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <circle 
                                                                                className="opacity-25" 
                                                                                cx="12" 
                                                                                cy="12" 
                                                                                r="10" 
                                                                                stroke="currentColor" 
                                                                                strokeWidth="4"
                                                                            />
                                                                            <path 
                                                                                className="opacity-75" 
                                                                                fill="currentColor" 
                                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                            />
                                                                        </svg>
                                                                    )}
                                                                    {item.status === 'success' && (
                                                                        <CheckCircleIcon className="h-5 w-5 text-emerald" />
                                                                    )}
                                                                    {item.status === 'error' && (
                                                                        <XCircleIcon className="h-5 w-5 text-red" />
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Subtitle */}
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {formatItemSubtitle(item)}
                                                                </div>
                                                                
                                                                {/* Message */}
                                                                {item.message && (
                                                                    <div className={`text-xs mt-1 ${
                                                                        item.status === 'success' ? 'text-green-700' : 'text-red'
                                                                    }`}>
                                                                        {item.message}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Status Badge */}
                                                            <div className="ml-2 flex-shrink-0">
                                                                <Badge color={statusConfig.color}>
                                                                    {statusConfig.label}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>

                                    {/* Progress Summary */}
                                    {isProcessing && items.length > 0 && (
                                        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                                            <div className="flex items-center justify-between text-sm mb-2">
                                                <span className="text-blue-700 font-medium">
                                                    {t('Progress')}: {completedItems} / {items.length}
                                                </span>
                                                <span className="text-blue-700 font-semibold">
                                                    {progressPercentage}%
                                                </span>
                                            </div>
                                            
                                            {/* Animated Progress Bar */}
                                            <div className="relative">
                                                <div className="bg-white rounded-full h-2.5 overflow-hidden shadow-inner">
                                                    <div
                                                        className="h-2.5 rounded-full transition-all duration-500 ease-out relative overflow-hidden bg-gradient-to-r from-primary via-primary to-primary/80"
                                                        style={{ 
                                                            width: `${progressPercentage}%`,
                                                            boxShadow: '0 0 8px rgba(40, 95, 116, 0.3)'
                                                        }}
                                                    >
                                                        {/* Moving shine effect */}
                                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer" />
                                                        
                                                        {/* Pulsing glow overlay */}
                                                        <div className="absolute inset-0 bg-white opacity-10 animate-pulse-glow" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            {!isProcessing && allPending && (
                                <>
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-red px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                        onClick={handleConfirm}
                                    >
                                        {confirmText}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={handleClose}
                                    >
                                        {cancelText}
                                    </button>
                                </>
                            )}
                            
                            {!isProcessing && !allPending && (
                                <button
                                    type="button"
                                    className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 sm:w-auto"
                                    onClick={handleClose}
                                >
                                    {closeText}
                                </button>
                            )}
                            
                            {isProcessing && (
                                <div className="text-sm text-gray-500 flex items-center gap-2">
                                    <svg 
                                        className="animate-spin h-5 w-5 text-primary" 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        fill="none" 
                                        viewBox="0 0 24 24"
                                    >
                                        <circle 
                                            className="opacity-25" 
                                            cx="12" 
                                            cy="12" 
                                            r="10" 
                                            stroke="currentColor" 
                                            strokeWidth="4"
                                        />
                                        <path 
                                            className="opacity-75" 
                                            fill="currentColor" 
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    {t('Please wait, processing...')}
                                </div>
                            )}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}
